import React, { useContext, useEffect, useState } from 'react';
import {
  CFormGroup,
  CInput,
  CLabel,
  CSelect,
  CSwitch,
  CTextarea,
  CRow,
  CCol,
  CButton,
  CTooltip,
  CImg,
  CFormText,
  CAlert,
} from '@coreui/react';
import classNames from 'classnames';
import * as pluralize from 'pluralize';
import AsyncSelect from 'react-select/async';
import { debounce, get, capitalize } from 'lodash';
import moment from 'moment-timezone';
import { FormikProps, FieldArray, Field } from 'formik';
import PhoneInput from 'react-phone-number-input';
import Flatpickr from "react-flatpickr";
import CIcon from '@coreui/icons-react';
import { useSelector } from 'react-redux';
import { GeocodeResult } from '../../shared/models/geocoder';
import { JobPayload } from '../../shared/models/jobs';
import GeocoderService from '../../shared/services/geocoder.service';
import { DEFAULT_TECHNICIAN, UserInfo } from '../../shared/models/user';
import tooltip from '../../assets/images/tool_tip.svg';

import "flatpickr/dist/themes/light.css";
import { ConfigContext } from '../../shared/contexts/journeyConfig.context';
import COUNTRIES from '../../shared/constants/phone-countries.constants';
import { selectOrgHierarchySelectedOrg } from '../../store/selectors/orgHierarchy.selectors';
import { formatHereAddressLabel } from '../../shared/utils/heremap-utils';
import renderError from '../../shared/utils/error-utils';

const ADDRESS_MAX_COUNT = 2;
const ADDRESS_MIN_COUNT = 1;
const BASE_LOCATION = { lat: 47.6078397, lng: -122.336489 };
const INVALID_DATE = "Invalid Date";

const DATE_TIME_PICKER_OPTIONS = () => ({
  dateFormat: 'M/D/Y h:mm A',
  minuteIncrement: 30,
  minDate: 'today',
  allowInvalidPreload: true,
  formatDate: (date, format) => {
    return `${moment(date).format(format)  } ${  moment.tz(moment.tz.guess()).zoneAbbr()}`;
  },
  defaultHour: new Date().getHours(),
  defaultMinute: new Date().getMinutes()
});

export enum JobFormMode {
  Add,
  Edit,
  EditStartedJob
}

export interface JobFormProps {
  isEdit: boolean;
  formik: FormikProps<JobPayload>;
  technicians: UserInfo[];
  mode?: JobFormMode;
}

const JobForm = (props: JobFormProps) => {
  const { isEdit, formik, technicians, mode } = props;
  const labels = useContext(ConfigContext);
  const baseLocation = useSelector(selectOrgHierarchySelectedOrg)?.base_location || BASE_LOCATION;

  const [showAppointmentWindow, setShowAppointmentWindow] = useState(false);

  const destinationOptions = debounce((value: string, callback) => {
    GeocoderService.search(value, `${baseLocation?.lat},${baseLocation?.lng}`)
      .then(results => {
        callback(results);
      });
  }, 300);

  const isInvalid = (fieldName: string) => get(formik.touched, fieldName) && get(formik.errors, fieldName);

  useEffect(() => {
    const apptTo = formik.values.appointment?.to;
    const apptFrom = formik.values.appointment?.from;
    if (apptTo && apptTo.toString() !== INVALID_DATE && apptTo?.getTime() !== apptFrom?.getTime()) {
      setShowAppointmentWindow(true);
    }
  },[formik.values.appointment.to]);

  useEffect(() => {
    const apptTo = formik.values.appointment?.to;
    const apptFrom = formik.values.appointment?.from;
    if (apptFrom && apptFrom.toString() !== INVALID_DATE && apptFrom?.getTime() > apptTo?.getTime() && !showAppointmentWindow) {
      formik.setFieldValue('appointment.to', apptFrom);
    }
  },[formik.values.appointment.from]);

  useEffect(() => {
    const apptFrom = formik.values.appointment?.from;
    if (!showAppointmentWindow) {
      formik.setFieldValue('appointment.to', apptFrom);
    }
  },[showAppointmentWindow]);


  const toggleShowCoordinates = (value) => {
    formik.setFieldValue('destination.showCoordinates', value);
  };

  return (
    <>
      <CAlert color="secondary" className='mb-2'>It might take up to 5 seconds before a new {labels.task} will be shown on the {pluralize(capitalize(labels.task))} page.</CAlert>
      <CFormGroup className="d-flex glympser-alignEnd">
        <span className="mr-1">
          Arrival Window?
        </span>
        <CTooltip placement="top" content="Allows the user to specify a promised arrival time window.">
          <CImg src={tooltip} className="glympser-toolTip mr-2 mb-2"/>
        </CTooltip>
        <CSwitch onChange={ (event: React.ChangeEvent<HTMLInputElement>) => setShowAppointmentWindow(event.target.checked) } checked={showAppointmentWindow}/>
      </CFormGroup>

      <CFormGroup>
        <CLabel htmlFor="appointment.from">{showAppointmentWindow ? 'Arrival Window Start' : 'Promised Time'}</CLabel>
        {
          !showAppointmentWindow ? (
            <CTooltip placement="top" content="Promised arrival time">
              <CImg src={tooltip} className="glympser-toolTip ml-1 mb-2"/>
            </CTooltip>
          ) : null
        }
        <Flatpickr
          id="appointment.from"
          placeholder={showAppointmentWindow ? 'Arrival Window Start' : 'Promised Time'}
          name="appointment.from"
          data-enable-time
          options={ DATE_TIME_PICKER_OPTIONS() }
          value={ formik.values.appointment.from }
          onChange={ (value) => formik.setFieldValue('appointment.from', value[0]) }
          className={classNames('form-control datetimepicker', {'is-invalid': isInvalid('appointment.from')})}
        />
        {isInvalid('appointment.from') && <div className="invalid-feedback">{ renderError(formik.errors.appointment.from) }</div>}
      </CFormGroup>

      { showAppointmentWindow && (
        <CFormGroup>
          <CLabel htmlFor="appointment.to">Arrival Window End</CLabel>
          <Flatpickr
            id="appointment.to"
            placeholder="Arrival Window End"
            name="appointment.to"
            data-enable-time
            options={ DATE_TIME_PICKER_OPTIONS() }
            value={ formik.values.appointment.to }
            onChange={ (value) => formik.setFieldValue('appointment.to', value[0]) }
            className={classNames('form-control datetimepicker', {'is-invalid': isInvalid('appointment.to')})}
          />
          {isInvalid('appointment.to') && <div className="invalid-feedback">{ renderError(formik.errors.appointment.to) }</div>}
        </CFormGroup>
      ) }

      <CFormGroup className="d-flex glympser-alignEnd">
        <span className="mr-1">
          Use Latitude and Longitude?
        </span>
        <CTooltip placement="top" content="Replaces Address search. Used when the destination is not connected to an actual address (e.g. a specific area of land in an orchard)">
          <CImg src={tooltip} className="glympser-toolTip mr-2 mb-2"/>
        </CTooltip>
        <CSwitch onChange={ (event: React.ChangeEvent<HTMLInputElement>) => toggleShowCoordinates(event.target.checked) }  checked={formik.values.destination.showCoordinates}/>
      </CFormGroup>

      { !formik.values.destination.showCoordinates && (
        <CFormGroup>
          <CLabel htmlFor="destination">Address</CLabel>
          <CTooltip placement="top" content="Customer's address">
            <CImg src={tooltip} className="glympser-toolTip ml-1 mb-2"/>
          </CTooltip>
          <AsyncSelect
            id="destination"
            name="destination"
            value={ formik.values.destination }
            loadOptions={ destinationOptions }
            getOptionLabel={ (value: GeocodeResult) => formatHereAddressLabel(value.address) }
            getOptionValue={ (value: GeocodeResult) => formatHereAddressLabel(value.address) }
            onChange={ ({ address, name}) => {
              formik.setFieldValue('destination', {
                ...formik.values.destination,
                address: formatHereAddressLabel(address),
                name
              })
            } }
            placeholder={ false }
            className={classNames({'is-invalid': isInvalid('destination.address')})}
            styles={{
              control: (provided) => ( isInvalid('destination.address') ? {
                ...provided,
                borderColor: '#e55353 !important',
              } : provided)
            }}
          />
          {isInvalid('destination.address') && <div className="invalid-feedback">{ formik.errors.destination.address }</div>}
        </CFormGroup>
      ) }

      { formik.values.destination.showCoordinates && (
        <>
          <CFormGroup>
            <CLabel htmlFor="destination.name">Destination Name</CLabel>
            <CInput
              id="destination.name"
              type="text"
              placeholder="Destination Name"
              name="destination.name"
              className={classNames({'is-invalid': isInvalid('destination.name')})}
              { ...formik.getFieldProps('destination.name') }
            />
            {isInvalid('destination.name') && <div className="invalid-feedback">{ formik.errors.destination.name }</div>}
          </CFormGroup>
          <CRow>
            <CCol md={ 6 }>
              <CFormGroup>
                <CLabel htmlFor="destination.lat">Latitude</CLabel>
                <CInput
                  id="destination.lat"
                  type="number"
                  placeholder="Latitude"
                  name="destination.lat"
                  className={classNames({'is-invalid': isInvalid('destination.lat')})}
                  { ...formik.getFieldProps('destination.lat') }
                />
                {isInvalid('destination.lat') && <div className="invalid-feedback">{ formik.errors.destination.lat }</div>}
              </CFormGroup>
            </CCol>
            <CCol md={ 6 }>
              <CFormGroup>
                <CLabel htmlFor="destination.lng">Longitude</CLabel>
                <CInput
                  id="destination.lng"
                  type="number"
                  placeholder="Longitude"
                  name="destination.lng"
                  className={classNames({'is-invalid': isInvalid('destination.lng')})}
                  { ...formik.getFieldProps('destination.lng') }
                />
                {isInvalid('destination.lng') && <div className="invalid-feedback">{ formik.errors.destination.lng }</div>}
              </CFormGroup>
            </CCol>
          </CRow>
        </>
      ) }

      <CFormGroup>
        <CLabel htmlFor="appointment_id">{ labels.task } ID</CLabel>
        <CTooltip placement="top" content={`<strong>Optional</strong>. This can be your own ${labels.task.toLowerCase()} number`}>
          <CImg src={tooltip} className="glympser-toolTip ml-1 mb-2"/>
        </CTooltip>
        <CInput
          id="appointment_id"
          type="text"
          placeholder={ `${ labels.task } ID` }
          name="appointment_id"
          { ...formik.getFieldProps('appointment.id') }
        />
      </CFormGroup>

      <CFormGroup>
        <CLabel htmlFor="description">{labels.task} Description</CLabel>
        <CTextarea
          id="name"
          type="text"
          placeholder="Description"
          name="description"
          className={classNames({'is-invalid': isInvalid('description')})}
          { ...formik.getFieldProps('description') }
        />
        {isInvalid('description') && <div className="invalid-feedback">{ formik.errors.description }</div>}
      </CFormGroup>

      <CFormGroup>
        <CLabel htmlFor="customer.name">Customer Name</CLabel>
        <CTooltip placement="top" content={`Name of the customer for this ${labels.task.toLowerCase()}`}>
          <CImg src={tooltip} className="glympser-toolTip ml-1 mb-2"/>
        </CTooltip>
        <CInput
          id="customer.name"
          type="text"
          placeholder="Customer Name"
          name="customer.name"
          className={classNames({'is-invalid': isInvalid('customer.name')})}
          { ...formik.getFieldProps('customer.name') }
          disabled={mode === JobFormMode.EditStartedJob}
        />
        {isInvalid('customer.name') && <div className="invalid-feedback">{ formik.errors.customer.name }</div>}
      </CFormGroup>

      <FieldArray
        name="customer.phones"
        render={arrayHelpers => {
          const {
            phones
          } = formik.values.customer;
          return (
            <CFormGroup>
              <CLabel className="mb-0" htmlFor="customer.phones[0]">Customer Phone Number(s)</CLabel>
              <CTooltip placement="bottom" content={`Phone number(s) that will be used for text notifications for this ${labels.task.toLowerCase()}`}>
                <CImg src={tooltip} className="glympser-toolTip ml-1 mb-2" name="cilInfo"/>
              </CTooltip>
              <CFormText className="mt-0 mb-1">By providing a phone number, your customer will opt-in to SMS notifications regarding this appointment</CFormText>
            {
              phones.map((phone, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <div key={index} className="mb-1">
                  <Field name={`customer.phones.${index}`} >
                    {({ field }) => (
                      <>
                        <div className="d-flex">
                          {/* @ts-ignore */}
                          <PhoneInput
                            id="customer.phone"
                            type="text"
                            placeholder="Customer Phone"
                            name={`customer.phones[${index}]`}
                            value={ field.value }
                            countries={COUNTRIES}
                            addInternationalOption={false}
                            onChange={ (value) => formik.setFieldValue(`customer.phones[${index}]`, value || '') }
                            className={classNames('form-control', {
                              'is-invalid': isInvalid(`customer.phones[${index}]`)
                            })}
                            onBlur={formik.getFieldProps(`customer.phones[${index}]`).onBlur}
                            defaultCountry="US"
                          />
                          {
                            phones.length === ADDRESS_MIN_COUNT && <CButton
                              size="sm"
                              variant="outline"
                              onClick={() => arrayHelpers.insert(index + 1, '')}
                            >
                              <CIcon size="sm" name="cil-plus" />
                            </CButton>
                          }
                          {
                            phones.length === ADDRESS_MAX_COUNT && <CButton
                              size="sm"
                              variant="outline"
                              onClick={() => arrayHelpers.remove(index)}
                            >
                              <CIcon size="sm" name="cil-minus" />
                            </CButton>
                          }
                        </div>
                        <div className={classNames('invalid-feedback', {'d-block': isInvalid(`customer.phones[${index}]`)})}>
                          { formik.errors.customer?.phones?.[index] }
                        </div>
                      </>
                    )}
                  </Field>
                </div>
              ))}
          </CFormGroup>);
        }}
      />

      <FieldArray
        name="customer.emails"
        render={arrayHelpers => {
          const {
            emails
          } = formik.values.customer;
          return (
            <CFormGroup>
              <CLabel htmlFor="customer.emails[0]">Customer Email(s)</CLabel>
              <CTooltip placement="bottom" content={`Email address that will be used for notifications for this ${labels.task.toLowerCase()}`}>
                <CImg src={tooltip} className="glympser-toolTip ml-1 mb-2" name="cilInfo"/>
              </CTooltip>
              {
                emails.map((email, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <div key={index} className="mb-1">
                    <Field name={`customer.emails.${index}`} >
                      {({ field }) => (
                        <>
                          <div className="d-flex">
                            <CInput
                              id={`customer.emails[${index}]`}
                              type="email"
                              placeholder="Customer Email"
                              name={field.name}
                              value={field.value}
                              onChange={field.onChange}
                              onBlur={field.onBlur}
                              className={classNames({'is-invalid': isInvalid(`customer.emails[${index}]`)})}
                            />
                            {
                              emails.length === ADDRESS_MIN_COUNT && <CButton
                                size="sm"
                                variant="outline"
                                onClick={() => arrayHelpers.insert(index + 1, '')}
                              >
                                <CIcon size="sm" name="cil-plus" />
                              </CButton>
                            }
                            {
                              emails.length === ADDRESS_MAX_COUNT && <CButton
                                size="sm"
                                variant="outline"
                                onClick={() => arrayHelpers.remove(index)}
                              >
                                <CIcon size="sm" name="cil-minus" />
                              </CButton>
                            }
                          </div>
                          <div className={classNames('invalid-feedback', {'d-block': isInvalid(`customer.emails[${index}]`)})}>
                            { formik.errors.customer?.emails?.[index] }
                          </div>
                        </>
                      )}
                    </Field>
                  </div>
                ))}
            </CFormGroup>);
        }}
      />

      { !isEdit && (
        <CFormGroup>
          <CLabel htmlFor="technician">{labels.agent}</CLabel>
          <CTooltip placement="top" content={`Assign a ${labels.agent.toLowerCase()} to this ${labels.task.toLowerCase()}`}>
            <CImg src={tooltip} className="glympser-toolTip ml-1 mb-2"/>
          </CTooltip>
          <CSelect
            id="technician"
            name="technician"
            className={classNames({'is-invalid': isInvalid('technician')})}
            { ...formik.getFieldProps('technician') }
          >
            <option value={ DEFAULT_TECHNICIAN }>Unassigned</option>
            { technicians?.map(tech => <option key={ tech.id } value={ tech.id }>{ tech.name }</option>) }
          </CSelect>
          {isInvalid('technician') && <div className="invalid-feedback">{ formik.errors.technician }</div>}
        </CFormGroup>
      ) }
    </>
  );
};

JobForm.defaultProps = {
  mode: JobFormMode.Add
};

export default JobForm;
